import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Link, createFileRoute, useRouter } from "@tanstack/react-router";
import { LockIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { userLoginMutation } from "@/generated/api/@tanstack/react-query.gen";
import { capitalizeFirstLetter } from "@/lib/utils.ts";
import {
  CardAuth,
  CardAuthContent,
  CardAuthDescription,
  CardAuthFooter,
  CardAuthHeader,
  CardAuthTitle,
} from "@/components/ui/card-auth";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import { Input } from "@/components/ui/input.tsx";
import { ButtonIcon } from "@/components/button-icon.tsx";
import { TLoginForm, loginFormSchema } from "./_data/login-schema";

const searchSchema = z.object({
  callbackUrl: z.string().optional(),
});

export const Route = createFileRoute("/(guest)/login/")({
  component: RouteComponent,
  validateSearch: (search) => searchSchema.parse(search),
});

function RouteComponent() {
  const router = useRouter();
  const { callbackUrl } = Route.useSearch();

  const form = useForm({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const { mutate, isPending } = useMutation({
    ...userLoginMutation({
      headers: {
        "Content-Type": "application/json",
      },
    }),
    onError: (error) => {
      const errorStatus = error.status;

      if (errorStatus === 403) {
        if (error.response?.data?.code === "mfa_required") {
          router.navigate({
            to: `/two-factor`,
            replace: true,
            search: callbackUrl ? { callbackUrl } : undefined,
          });
          return;
        }
        if (error.response?.data?.code === "mfa_missing") {
          router.navigate({
            to: `/setting-mfa`,
            replace: true,
            search: callbackUrl ? { callbackUrl } : undefined,
          });
          return;
        }
      }
      if (errorStatus === 400) {
        toast.error("Invalid credentials");
        return;
      }
      if (errorStatus === 429) {
        toast.error("Too many login attempts");
        return;
      }
      toast.error(capitalizeFirstLetter(error.message));
    },
  });

  function onSubmit(values: TLoginForm) {
    mutate({ body: values });
  }

  return (
    <CardAuth>
      <CardAuthHeader>
        <CardAuthTitle>
          <span>Log in to</span>{" "}
          <span className="text-primary font-semibold">Fastboy Payments</span>
        </CardAuthTitle>
        <CardAuthDescription className="text-gray-500">
          Secure access to your financial dashboard
        </CardAuthDescription>
      </CardAuthHeader>
      <CardAuthContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-gray-700">Username</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter your username"
                      className="h-11 px-4"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-sm" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-gray-700">Password</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter your password"
                      type="password"
                      className="h-11 px-4"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-sm" />
                </FormItem>
              )}
            />
            <ButtonIcon
              className="h-11 w-full text-base font-medium"
              type="submit"
              loading={isPending}
            >
              Login
            </ButtonIcon>
          </form>
        </Form>
      </CardAuthContent>
      <CardAuthFooter className="flex flex-col items-center gap-5">
        <Link
          to="/forgot-password"
          className="text-primary hover:text-primary/90 text-sm font-medium transition-colors hover:underline"
        >
          Forgot your password?
        </Link>
        <div className="flex items-center text-xs text-gray-500">
          <LockIcon className="text-primary mr-1.5 size-3.5" />
          Secure SSL encrypted connection
        </div>
      </CardAuthFooter>
    </CardAuth>
  );
}
